import { useIsMobile, useTranslation } from '@obvio/app'
import { SvgArrowDown, SvgArrowUp } from '@obvio/svg'
import { Section } from '@obvio/ui'
import { useTransform, useScroll } from 'framer-motion'
import { useMemo, useState } from 'react'

import { Art } from '../Art'
import {
  Item,
  MotionFade,
  MotionParallax,
  GridWrap,
  CenterButton,
  CenterStack,
} from '../FluidGrid/components'

import type { ArtData } from '../Art'
import type { StateSetter } from '@obvio/utils'
import type { Variants } from 'framer-motion'
import type { ReactElement } from 'react'

const eventVariants: Variants = {
  hidden: {
    opacity: 0,
  },
  inView: {
    opacity: 1,
    y: [50, 0],
  },
}

type EventProps = {
  index: number
  artwork: ArtData
  selectArt: StateSetter<string | null>
}

function Event({ index, selectArt, artwork }: EventProps) {
  const { scrollY } = useScroll()
  const isMobile = useIsMobile()
  const y = useTransform(
    scrollY,
    [-100, 200],
    [0, -10 - Math.floor(Math.random() * 5)],
    {
      clamp: false,
    },
  )
  const even = useMemo(() => index % 2, [index])
  return (
    <Item key={index} noCenter $row={index * 3 + 1}>
      <MotionFade
        initial="hidden"
        whileInView="inView"
        variants={eventVariants}
        style={{ marginLeft: even ? 'auto' : 0 }}
        transition={{ duration: 1 }}
        viewport={{ once: true }}
      >
        <MotionParallax initial={{ y: 0 }} style={{ y: isMobile ? 0 : y }}>
          <Art artwork={artwork} selectArt={selectArt} />
        </MotionParallax>
      </MotionFade>
    </Item>
  )
}

type EventArtGridProps = {
  selectArt: StateSetter<string | null>
  artworks: ArtData[]
}

export function EventArtGrid({
  selectArt,
  artworks,
}: EventArtGridProps): ReactElement {
  const { t } = useTranslation()
  const [displayAll, setDisplayAll] = useState(artworks.length === 6)
  const events = useMemo(
    () =>
      (displayAll ? artworks : artworks.slice(0, 6)).map((artwork, i) => (
        <Event
          key={artwork.id}
          index={i}
          selectArt={selectArt}
          artwork={artwork}
        />
      )),
    [artworks, displayAll, selectArt],
  )
  const toggle = () => setDisplayAll((v) => !v)
  const expandable = artworks.length > 6

  return (
    <Section>
      <GridWrap templateColumns="repeat(2, 1fr)" gap="medium">
        {events}
      </GridWrap>
      {expandable ? (
        <CenterButton kind="transparent" onClick={toggle}>
          <CenterStack spacing="extraSmall">
            <span>{t(displayAll ? 'label.collapse' : 'label.expand')}</span>
            {displayAll ? (
              <SvgArrowUp height="1.2rem" width="1.2rem" strokeWidth="1px" />
            ) : (
              <SvgArrowDown height="1.2rem" width="1.2rem" strokeWidth="1px" />
            )}
          </CenterStack>
        </CenterButton>
      ) : null}
    </Section>
  )
}
